import {
  Box,
  Stack,
  Radio,
  FormLabel,
  IconButton,
  RadioGroup,
  FormControl,
  FormControlLabel
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { Image } from "components/Image";
import { blurredImageStyles } from "components/carousel/CarouselCard";
import type { ChangeEvent } from "react";

export const LISTING_PICTURES_VISIBILITY = ["all", "none", "some"] as const;

export type ListingPicturesVisibility = (typeof LISTING_PICTURES_VISIBILITY)[number];

export type ListingVisibilityPicture = { id: string; src: string; alt: string };

type ListingPicturesVisibilityControlsProps = {
  pictures: ListingVisibilityPicture[] | null;
  mode: ListingPicturesVisibility;
  /** Picture srcs that are blurred — only relevant when mode === "some". */
  hiddenPictures: string[];
  onModeChange: (mode: ListingPicturesVisibility) => void;
  onTogglePicture: (src: string) => void;
};

/**
 * Controlled UI for choosing how a listing's photos appear in a minisite
 * (show all / hide all / blur some). Holds no state and performs no writes —
 * the parent owns `mode`/`hiddenPictures` and reacts to the callbacks. Reused by
 * both the per-minisite editor (writes to Firestore) and the customize-before-add
 * dialog (writes to local React state).
 */
export function ListingPicturesVisibilityControls({
  pictures,
  mode,
  hiddenPictures,
  onModeChange,
  onTogglePicture
}: ListingPicturesVisibilityControlsProps): JSX.Element {
  const { t: translate } = useTranslation();

  const isPicturesCanBeBlurred = mode === "some";

  const handleModeChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onModeChange(event.target.value as ListingPicturesVisibility);
  };

  return (
    <Stack gap={2}>
      <FormControl>
        <FormLabel sx={{ color: "black !important" }}>
          {translate("listingMatchToContactsPage.picturesVisibilityMode")}
        </FormLabel>
        <RadioGroup row value={mode} onChange={handleModeChange}>
          {LISTING_PICTURES_VISIBILITY.map((visibilityMode) => (
            <FormControlLabel
              key={visibilityMode}
              value={visibilityMode}
              control={<Radio />}
              label={translate(
                `listingMatchToContactsPage.picturesVisibilityModeLabel.${visibilityMode}`
              )}
            />
          ))}
        </RadioGroup>
      </FormControl>
      <Stack direction="row" flexWrap="wrap" gap={2} justifyContent="center">
        {pictures?.map(({ id, src, alt }) => {
          let isImageBlurred = hiddenPictures.includes(src);

          if (mode === "all") {
            isImageBlurred = false;
          } else if (mode === "none") {
            isImageBlurred = true;
          }

          return (
            <Box sx={{ position: "relative" }} key={id}>
              <Image
                sx={{
                  width: 128,
                  height: 128,
                  borderRadius: 1,
                  transition: "all 200ms",
                  ...(isImageBlurred && blurredImageStyles)
                }}
                src={src}
                alt={alt}
                cropTopImage
              />
              {isPicturesCanBeBlurred && (
                <IconButton
                  sx={{
                    m: 1,
                    top: 0,
                    right: 0,
                    color: "white",
                    border: 1,
                    position: "absolute",
                    bgcolor: "rgba(0, 0, 0, 0.4)",
                    borderColor: "divider",
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.6)"
                    }
                  }}
                  onClick={() => onTogglePicture(src)}
                >
                  {isImageBlurred ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              )}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
